import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, MessageSquare, ChevronDown } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { sendContactMessage } from '../services/contactService';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.3s',
        boxShadow: open ? '0 8px 24px rgba(0,0,0,0.06)' : '0 2px 8px rgba(0,0,0,0.02)',
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1rem',
          fontWeight: 600,
          color: 'var(--color-gray-800)',
          textAlign: 'left',
        }}
      >
        {question}
        <ChevronDown
          size={18}
          style={{
            color: 'var(--color-gray-400)',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.3s',
            flexShrink: 0,
            marginLeft: '12px',
          }}
        />
      </button>
      <div
        style={{
          maxHeight: open ? '200px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.4s cubic-bezier(.22,1,.36,1)',
        }}
      >
        <p style={{ padding: '0 24px 20px', color: 'var(--color-gray-500)', lineHeight: 1.7, fontSize: '0.95rem' }}>
          {answer}
        </p>
      </div>
    </div>
  );
};

const faqs = [
  {
    question: 'What services does FretDev offer?',
    answer: 'We deliver enterprise-grade AI and cloud engineering to small and medium businesses. Our team\'s experience spans Fortune 500 companies and government agencies. Services include AI workflow automation, chatbots and knowledge assistants, cloud architecture on AWS, custom business applications, data dashboards, and AI strategy consulting.',
  },
  {
    question: 'What is a typical project timeline?',
    answer: 'A GenAI proof-of-concept typically takes 2–4 weeks. Full production deployments run 2–4 months depending on scope. We provide a detailed roadmap with clear milestones and transparent pricing before any work begins.',
  },
  {
    question: 'How does pricing work?',
    answer: 'We offer both fixed-price and hourly engagement models. Every project begins with a detailed scope and transparent quote — you know exactly what you\'re paying for before we write a single line of code. Our pricing reflects enterprise-quality delivery at a scale that works for growing businesses.',
  },
  {
    question: 'What technologies do you work with?',
    answer: 'Our core stack includes Python, AWS (Bedrock, Lambda, ECS, CDK), Spring Boot, Node.js, React, and LLM frameworks like LangChain. Our team holds 9 professional certifications and brings over a decade of experience from Fortune 500 companies and government agencies.',
  },
];

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await sendContactMessage(formData);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '2px solid var(--color-gray-200)',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: '#fff',
    fontFamily: 'inherit',
  };

  return (
    <div>
      {/* ─── Hero ─── */}
      <section
        className="hero-fade-in hero-padding-lg"
        style={{
          background: 'linear-gradient(135deg, #fafafa, #f0f0f5)',
          borderRadius: '24px',
          marginBottom: '48px',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 800,
            fontFamily: 'var(--font-heading)',
            marginBottom: '16px',
          }}
        >
          Let's Discuss Your Next Project
        </h1>
        <p
          style={{
            fontSize: '1.1rem',
            color: 'var(--color-gray-500)',
            lineHeight: 1.7,
            maxWidth: '560px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          Whether you're exploring Generative AI for the first time or ready to modernize your workflows —
          our team brings enterprise and government-grade engineering expertise to every conversation.
        </p>
      </section>

      {/* ─── Main Grid ─── */}
      <div
        className="contact-layout"
        style={{
          marginBottom: '64px',
        }}
      >
        {/* Sidebar */}
        <AnimatedSection>
          <div
            style={{
              position: 'sticky',
              top: '100px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {[
              { icon: <Mail size={20} />, label: 'Email', value: 'info@fretdev.com' },
              { icon: <MapPin size={20} />, label: 'Location', value: 'United States' },
              { icon: <Clock size={20} />, label: 'Response Time', value: 'Within 24 hours' },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  background: '#fff',
                  borderRadius: '14px',
                  padding: '20px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                }}
              >
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '12px',
                    background: 'rgba(94,96,206,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-primary)',
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                <div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-gray-400)', marginBottom: '2px' }}>{item.label}</p>
                  <p style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-gray-800)' }}>{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Form */}
        <AnimatedSection delay={150}>
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              padding: '40px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            }}
          >
            {submitSuccess ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <CheckCircle size={48} style={{ color: '#22c55e', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '8px' }}>Message Sent!</h3>
                <p style={{ color: 'var(--color-gray-500)' }}>We'll get back to you within 24 hours.</p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  style={{
                    marginTop: '24px',
                    padding: '12px 28px',
                    background: 'var(--gradient-primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                  }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-row" style={{ marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-gray-700)' }}>Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                      placeholder="Your name"
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-gray-200)'; }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-gray-700)' }}>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                      placeholder="you@company.com"
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-gray-200)'; }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-gray-700)' }}>Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder="Project inquiry"
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-gray-200)'; }}
                  />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-gray-700)' }}>Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    placeholder="Tell us about your project..."
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-gray-200)'; }}
                  />
                </div>

                {submitError && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', marginBottom: '16px', fontSize: '0.9rem' }}>
                    <AlertCircle size={16} /> {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: isSubmitting ? 'var(--color-gray-300)' : 'var(--gradient-primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.25s',
                    boxShadow: '0 4px 15px rgba(94,96,206,0.3)',
                  }}
                >
                  {isSubmitting ? 'Sending...' : <><Send size={18} /> Send Message</>}
                </button>
              </form>
            )}
          </div>
        </AnimatedSection>
      </div>

      {/* ─── FAQ ─── */}
      <AnimatedSection>
        <section
          className="section-padding-lg"
          style={{
            background: 'linear-gradient(135deg, #fafafa, #f0f0f5)',
            borderRadius: '24px',
            marginBottom: '32px',
          }}
        >
          <div className="text-center" style={{ marginBottom: '40px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(94,96,206,0.1)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              <MessageSquare size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '6px' }} />
              FAQ
            </span>
            <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
          </div>
          <div style={{ maxWidth: '700px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Contact;
